// Production endpoint validation for the customer web application.
//
// A customers-realm access token is tenant-scoped, but it is still an
// authenticated credential. Development may use local Keycloak and
// platform-graph defaults; a production bundle must never silently redirect
// or attach that token to a loopback/HTTP endpoint when public Vite
// configuration was omitted.

export type CustomerDeploymentMode = "development" | "production";

export interface CustomerEndpointEnvironment {
  readonly configuredUrl?: string | undefined;
  readonly mode: CustomerDeploymentMode;
}

function isLocalOrUnspecifiedHostname(hostname: string): boolean {
  const normalized = hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, "")
    .replace(/\.$/, "");
  const mappedIpv4HighWord = /^::ffff:([0-9a-f]{1,4}):[0-9a-f]{1,4}$/.exec(
    normalized,
  )?.[1];
  const ipv4Octets = normalized.split(".");
  const isThisNetworkIpv4 =
    ipv4Octets.length === 4 &&
    ipv4Octets.every((octet) => /^\d{1,3}$/.test(octet)) &&
    Number(ipv4Octets[0]) === 0;

  return (
    normalized === "localhost" ||
    normalized.endsWith(".localhost") ||
    normalized === "::1" ||
    normalized === "::" ||
    isThisNetworkIpv4 ||
    /^127(?:\.\d{1,3}){0,3}$/.test(normalized) ||
    (mappedIpv4HighWord !== undefined &&
      [0, 127].includes(Number.parseInt(mappedIpv4HighWord, 16) >> 8))
  );
}

function resolveUrl(
  environment: CustomerEndpointEnvironment,
  envName: string,
  developmentFallback: string,
): URL {
  const configured = environment.configuredUrl?.trim();
  const candidate =
    configured === undefined || configured.length === 0
      ? environment.mode === "development"
        ? developmentFallback
        : undefined
      : configured;
  if (candidate === undefined) {
    throw new Error(`${envName} is required in production`);
  }

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error(`${envName} must be a valid absolute URL`);
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`${envName} must use HTTP or HTTPS`);
  }
  if (url.username !== "" || url.password !== "" || url.search !== "" || url.hash !== "") {
    throw new Error(`${envName} must not contain credentials, query, or fragment`);
  }
  if (
    environment.mode === "production" &&
    (url.protocol !== "https:" || isLocalOrUnspecifiedHostname(url.hostname))
  ) {
    throw new Error(`${envName} must use a non-local HTTPS origin in production`);
  }

  return url;
}

/** Resolve the platform-graph API origin before the fetch client can attach a customer token. */
export function resolveCustomerPlatformGraphBaseUrl(
  environment: CustomerEndpointEnvironment,
): string {
  const url = resolveUrl(
    environment,
    "VITE_PLATFORM_GRAPH_BASE_URL",
    "http://localhost:3000",
  );
  if (url.pathname !== "/") {
    throw new Error(
      "VITE_PLATFORM_GRAPH_BASE_URL must be an origin without a path",
    );
  }
  return url.origin;
}

/** Resolve the exact customers-realm issuer before AuthProvider can redirect the browser. */
export function resolveCustomerKeycloakAuthority(
  environment: CustomerEndpointEnvironment,
): string {
  const url = resolveUrl(
    environment,
    "VITE_KEYCLOAK_AUTHORITY",
    "http://localhost:8080/realms/customers",
  );
  if (
    url.pathname !== "/realms/customers" &&
    url.pathname !== "/realms/customers/"
  ) {
    throw new Error(
      "VITE_KEYCLOAK_AUTHORITY must be the customers realm issuer (/realms/customers)",
    );
  }
  return `${url.origin}/realms/customers`;
}
